import React from "react";
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { COLLECTION_METRIC_NARROW_COLUMN_WIDTH, COLLECTION_FECHA_COLUMN_WIDTH, COLLECTION_TIPO_COLUMN_WIDTH, COLLECTION_IMAGEN_COLUMN_WIDTH, COLLECTION_MARCA_COLUMN_WIDTH, COLLECTION_TITLE_COLUMN_WIDTH, COLLECTION_POSICION_COLUMN_WIDTH, COLLECTION_PLACEMENT_COLUMN_WIDTH, COLLECTION_ACCOUNT_COLUMN_WIDTH, COLLECTION_DETALLES_COLUMN_WIDTH, COLLECTION_COMMENTS_COLUMN_LABEL } from "./collectionContentTableConstants";
import { collectionContenidoTableSx } from "./collectionContentTableLayout";

const CONTENT_TABLE_ROWS = 6;

const skeletonLineSx = { transform: "none" } as const;

function skeletonHeaderCellSx(width?: number) {
  return width
    ? { width, minWidth: width, maxWidth: width }
    : undefined;
}

function SkeletonHeaderRow({
  columns,
}: {
  columns: Array<{
    label: string;
    align: "left" | "right" | "center";
    width?: number;
  }>;
}) {
  return (
    <TableRow>
      {columns.map((col) => (
        <TableCell
          key={col.label}
          align={col.align}
          sx={skeletonHeaderCellSx(col.width)}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent:
                col.align === "right"
                  ? "flex-end"
                  : col.align === "center"
                    ? "center"
                    : "flex-start",
            }}
          >
            <Skeleton
              variant="text"
              width={
                col.width
                  ? col.width - 16
                  : col.label === "Mención"
                    ? 160
                    : 88
              }
              height={24}
              sx={skeletonLineSx}
            />
          </Box>
        </TableCell>
      ))}
    </TableRow>
  );
}

function TableSkeletonShell({
  children,
  embedded = false,
  tableSx,
}: {
  children: React.ReactNode;
  embedded?: boolean;
  tableSx?: Record<string, unknown>;
}) {
  const table = <Table sx={tableSx}>{children}</Table>;

  if (embedded) {
    return (
      <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
        {table}
      </TableContainer>
    );
  }

  return (
    <Card>
      <CardContent>
        <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
          {table}
        </TableContainer>
      </CardContent>
    </Card>
  );
}

/** Matches GroupContentEcosystem: Fecha, Tipo, Imagen, Título, Views, Likes, Comen., Detalles */
export function MiContenidoCollectionContentTableSkeleton({
  rows = 5,
  showAccountColumn = false,
}: {
  rows?: number;
  showAccountColumn?: boolean;
}) {
  return (
    <TableSkeletonShell tableSx={collectionContenidoTableSx}>
      <TableHead>
        <TableRow>
          {[
            { label: "Fecha", width: COLLECTION_FECHA_COLUMN_WIDTH },
            ...(showAccountColumn
              ? [{ label: "Cuenta", width: COLLECTION_ACCOUNT_COLUMN_WIDTH }]
              : []),
            { label: "Tipo", width: COLLECTION_TIPO_COLUMN_WIDTH },
            { label: "Imagen", align: "center" as const, width: COLLECTION_IMAGEN_COLUMN_WIDTH },
            { label: "Título" },
            { label: "Views", align: "right" as const, width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH },
            { label: "Likes", align: "right" as const, width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH },
            {
              label: COLLECTION_COMMENTS_COLUMN_LABEL,
              align: "right" as const,
              width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH,
            },
            { label: "", align: "right" as const, width: COLLECTION_DETALLES_COLUMN_WIDTH },
          ].map((col) => (
            <TableCell
              key={`${col.label || "detalles"}-${"width" in col ? col.width : "fluid"}`}
              align={"align" in col ? col.align : "left"}
              sx={
                "width" in col && col.width
                  ? skeletonHeaderCellSx(col.width)
                  : undefined
              }
            >
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rows }).map((_, row) => (
          <TableRow key={row} sx={{ height: 56 }}>
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_FECHA_COLUMN_WIDTH)}>
              <Skeleton variant="text" width={72} height={20} sx={skeletonLineSx} />
            </TableCell>
            {showAccountColumn && (
              <TableCell sx={skeletonHeaderCellSx(COLLECTION_ACCOUNT_COLUMN_WIDTH)}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={80} height={20} sx={skeletonLineSx} />
                </Stack>
              </TableCell>
            )}
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_TIPO_COLUMN_WIDTH)}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={80} height={20} sx={skeletonLineSx} />
              </Stack>
            </TableCell>
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_IMAGEN_COLUMN_WIDTH)}>
              <Skeleton
                variant="rectangular"
                width={40}
                height={40}
                sx={{ borderRadius: 1 }}
              />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width="90%" height={20} sx={skeletonLineSx} />
              <Skeleton variant="text" width="60%" height={18} sx={{ mt: 0.5, ...skeletonLineSx }} />
            </TableCell>
            <TableCell
              align="right"
              sx={skeletonHeaderCellSx(COLLECTION_METRIC_NARROW_COLUMN_WIDTH)}
            >
              <Skeleton variant="text" width={40} height={20} sx={{ ml: "auto", ...skeletonLineSx }} />
            </TableCell>
            <TableCell
              align="right"
              sx={skeletonHeaderCellSx(COLLECTION_METRIC_NARROW_COLUMN_WIDTH)}
            >
              <Skeleton variant="text" width={40} height={20} sx={{ ml: "auto", ...skeletonLineSx }} />
            </TableCell>
            <TableCell
              align="right"
              sx={skeletonHeaderCellSx(COLLECTION_METRIC_NARROW_COLUMN_WIDTH)}
            >
              <Skeleton variant="text" width={32} height={20} sx={{ ml: "auto", ...skeletonLineSx }} />
            </TableCell>
            <TableCell
              align="right"
              sx={skeletonHeaderCellSx(COLLECTION_DETALLES_COLUMN_WIDTH)}
            >
              <Skeleton
                variant="rounded"
                width={48}
                height={28}
                sx={{ ml: "auto" }}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </TableSkeletonShell>
  );
}

/** Matches PublicSponsorLink content metrics table core columns */
export function PublicReportContentTableSkeleton({
  embedded = false,
}: {
  embedded?: boolean;
}) {
  return (
    <TableSkeletonShell embedded={embedded} tableSx={collectionContenidoTableSx}>
      <TableHead>
        <TableRow>
          {[
            { label: "Fecha", align: "left" as const, width: COLLECTION_FECHA_COLUMN_WIDTH },
            { label: "Tipo", align: "left" as const, width: COLLECTION_TIPO_COLUMN_WIDTH },
            { label: "Imagen", align: "center" as const, width: COLLECTION_IMAGEN_COLUMN_WIDTH },
            { label: "Título", align: "left" as const },
            { label: "Views", align: "right" as const, width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH },
            { label: "Likes", align: "right" as const, width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH },
            { label: COLLECTION_COMMENTS_COLUMN_LABEL, align: "right" as const, width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH },
            { label: "", align: "right" as const, width: COLLECTION_DETALLES_COLUMN_WIDTH },
          ].map((col) => (
            <TableCell
              key={`${col.label || "detalles"}-${col.width ?? "fluid"}`}
              align={col.align}
              sx={
                col.width
                  ? {
                      width: col.width,
                      maxWidth: col.width,
                    }
                  : undefined
              }
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent:
                    col.align === "right" ? "flex-end" : "flex-start",
                }}
              >
                <Skeleton
                  variant="text"
                  width={
                    col.width
                      ? col.width - 16
                      : col.label === "Título"
                        ? 200
                        : 88
                  }
                  height={24}
                />
              </Box>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: CONTENT_TABLE_ROWS }).map((_, row) => (
          <TableRow key={row} sx={{ height: 56 }}>
            <TableCell sx={{ width: 90 }}>
              <Skeleton variant="text" width={72} height={20} />
            </TableCell>
            <TableCell sx={{ width: 100 }}>
              <Skeleton
                variant="rectangular"
                width={40}
                height={40}
                sx={{ borderRadius: 1 }}
              />
            </TableCell>
            <TableCell sx={{ minWidth: 200 }}>
              <Skeleton variant="text" width="90%" height={20} />
              <Skeleton variant="text" width="60%" height={18} sx={{ mt: 0.5 }} />
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={1} alignItems="center">
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={72} height={20} />
              </Stack>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH,
                maxWidth: COLLECTION_METRIC_NARROW_COLUMN_WIDTH,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Skeleton variant="text" width={48} height={20} />
              </Box>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH,
                maxWidth: COLLECTION_METRIC_NARROW_COLUMN_WIDTH,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Skeleton variant="text" width={40} height={20} />
              </Box>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: COLLECTION_METRIC_NARROW_COLUMN_WIDTH,
                maxWidth: COLLECTION_METRIC_NARROW_COLUMN_WIDTH,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Skeleton variant="text" width={32} height={20} />
              </Box>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: COLLECTION_DETALLES_COLUMN_WIDTH,
                maxWidth: COLLECTION_DETALLES_COLUMN_WIDTH,
              }}
            >
              <Skeleton
                variant="rounded"
                width={48}
                height={28}
                sx={{ ml: "auto" }}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </TableSkeletonShell>
  );
}

/** Matches GroupBrandIntegration "Detalle de menciones" table (Menciones de marca). */
export function BrandMentionsTableSkeleton({
  embedded = false,
  showAccountColumn = false,
}: {
  embedded?: boolean;
  showAccountColumn?: boolean;
}) {
  const mentionHeaders = [
    { label: "Fecha", align: "left" as const, width: COLLECTION_FECHA_COLUMN_WIDTH },
    ...(showAccountColumn
      ? [{ label: "Cuenta", align: "left" as const, width: COLLECTION_ACCOUNT_COLUMN_WIDTH }]
      : []),
    { label: "Tipo", align: "left" as const, width: COLLECTION_TIPO_COLUMN_WIDTH },
    { label: "Imagen", align: "center" as const, width: COLLECTION_IMAGEN_COLUMN_WIDTH },
    { label: "Marca", align: "center" as const, width: COLLECTION_MARCA_COLUMN_WIDTH },
    { label: "Título", align: "left" as const, width: COLLECTION_TITLE_COLUMN_WIDTH },
    { label: "Mención", align: "left" as const },
    { label: "Posición", align: "right" as const, width: COLLECTION_POSICION_COLUMN_WIDTH },
    { label: "", align: "right" as const, width: COLLECTION_DETALLES_COLUMN_WIDTH },
  ];

  return (
    <TableSkeletonShell embedded={embedded} tableSx={collectionContenidoTableSx}>
      <TableHead>
        <SkeletonHeaderRow columns={mentionHeaders} />
      </TableHead>
      <TableBody>
        {Array.from({ length: CONTENT_TABLE_ROWS }).map((_, row) => (
          <TableRow key={row} sx={{ height: 56 }}>
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_FECHA_COLUMN_WIDTH)}>
              <Skeleton variant="text" width={72} height={20} sx={skeletonLineSx} />
            </TableCell>
            {showAccountColumn && (
              <TableCell sx={skeletonHeaderCellSx(COLLECTION_ACCOUNT_COLUMN_WIDTH)}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={80} height={20} sx={skeletonLineSx} />
                </Stack>
              </TableCell>
            )}
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_TIPO_COLUMN_WIDTH)}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={72} height={20} sx={skeletonLineSx} />
              </Stack>
            </TableCell>
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_IMAGEN_COLUMN_WIDTH)}>
              <Skeleton
                variant="rectangular"
                width={40}
                height={40}
                sx={{ borderRadius: 1 }}
              />
            </TableCell>
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_MARCA_COLUMN_WIDTH)}>
              <Skeleton
                variant="rectangular"
                width={32}
                height={32}
                sx={{ borderRadius: 1 }}
              />
            </TableCell>
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_TITLE_COLUMN_WIDTH)}>
              <Skeleton variant="text" width="90%" height={20} sx={skeletonLineSx} />
              <Skeleton variant="text" width="60%" height={18} sx={{ mt: 0.5, ...skeletonLineSx }} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width="95%" height={20} sx={skeletonLineSx} />
              <Skeleton variant="text" width="80%" height={18} sx={{ mt: 0.5, ...skeletonLineSx }} />
              <Skeleton variant="text" width="55%" height={18} sx={{ mt: 0.5, ...skeletonLineSx }} />
            </TableCell>
            <TableCell
              align="right"
              sx={skeletonHeaderCellSx(COLLECTION_POSICION_COLUMN_WIDTH)}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Skeleton variant="text" width={56} height={20} sx={skeletonLineSx} />
              </Box>
            </TableCell>
            <TableCell
              align="right"
              sx={skeletonHeaderCellSx(COLLECTION_METRIC_NARROW_COLUMN_WIDTH)}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Skeleton
                  variant="rounded"
                  width={48}
                  height={28}
                  sx={skeletonLineSx}
                />
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </TableSkeletonShell>
  );
}

/** Matches GroupBrandIntegration "Presencia de marca en RRSS" table. */
export function BrandPresenceTableSkeleton({
  embedded = false,
  showAccountColumn = false,
}: {
  embedded?: boolean;
  showAccountColumn?: boolean;
}) {
  const presenceHeaders = [
    { label: "Fecha", align: "left" as const, width: COLLECTION_FECHA_COLUMN_WIDTH },
    ...(showAccountColumn
      ? [{ label: "Cuenta", align: "left" as const, width: COLLECTION_ACCOUNT_COLUMN_WIDTH }]
      : []),
    { label: "Tipo", align: "left" as const, width: COLLECTION_TIPO_COLUMN_WIDTH },
    { label: "Imagen", align: "center" as const, width: COLLECTION_IMAGEN_COLUMN_WIDTH },
    { label: "Marca", align: "center" as const, width: COLLECTION_MARCA_COLUMN_WIDTH },
    { label: "Título", align: "left" as const },
    { label: "Placement", align: "right" as const, width: COLLECTION_PLACEMENT_COLUMN_WIDTH },
    { label: "", align: "right" as const, width: COLLECTION_DETALLES_COLUMN_WIDTH },
  ];

  return (
    <TableSkeletonShell embedded={embedded} tableSx={collectionContenidoTableSx}>
      <TableHead>
        <SkeletonHeaderRow columns={presenceHeaders} />
      </TableHead>
      <TableBody>
        {Array.from({ length: CONTENT_TABLE_ROWS }).map((_, row) => (
          <TableRow key={row} sx={{ height: 56 }}>
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_FECHA_COLUMN_WIDTH)}>
              <Skeleton variant="text" width={72} height={20} sx={skeletonLineSx} />
            </TableCell>
            {showAccountColumn && (
              <TableCell sx={skeletonHeaderCellSx(COLLECTION_ACCOUNT_COLUMN_WIDTH)}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={80} height={20} sx={skeletonLineSx} />
                </Stack>
              </TableCell>
            )}
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_TIPO_COLUMN_WIDTH)}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={72} height={20} sx={skeletonLineSx} />
              </Stack>
            </TableCell>
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_IMAGEN_COLUMN_WIDTH)}>
              <Skeleton
                variant="rectangular"
                width={40}
                height={40}
                sx={{ borderRadius: 1 }}
              />
            </TableCell>
            <TableCell sx={skeletonHeaderCellSx(COLLECTION_MARCA_COLUMN_WIDTH)}>
              <Skeleton
                variant="rectangular"
                width={32}
                height={32}
                sx={{ borderRadius: 1 }}
              />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width="90%" height={20} sx={skeletonLineSx} />
              <Skeleton variant="text" width="60%" height={18} sx={{ mt: 0.5, ...skeletonLineSx }} />
            </TableCell>
            <TableCell
              align="right"
              sx={skeletonHeaderCellSx(COLLECTION_PLACEMENT_COLUMN_WIDTH)}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Skeleton variant="rounded" width={72} height={24} sx={skeletonLineSx} />
              </Box>
            </TableCell>
            <TableCell
              align="right"
              sx={skeletonHeaderCellSx(COLLECTION_METRIC_NARROW_COLUMN_WIDTH)}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Skeleton
                  variant="rounded"
                  width={48}
                  height={28}
                  sx={skeletonLineSx}
                />
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </TableSkeletonShell>
  );
}
